export function shouldPauseCreatorVideos(state) {
  return Boolean(!state.sectionVisible || !state.documentVisible);
}

function isMobileMedia() {
  return window.matchMedia('(max-width: 767px)').matches;
}

function syncVideoPoster(video) {
  if (!video) return;

  const host = video.closest('[data-responsive-video-poster]');
  if (!host) return;

  const poster = isMobileMedia()
    ? host.dataset.mobilePoster || host.dataset.poster || ''
    : host.dataset.poster || '';

  if (poster && video.getAttribute('poster') !== poster) {
    video.setAttribute('poster', poster);
  }
}

const elementName = 'aceii-creator-reactions';

if (
  typeof window !== 'undefined'
  && typeof HTMLElement !== 'undefined'
  && !window.customElements.get(elementName)
) {
  class AceiiCreatorReactions extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.cards = [...this.querySelectorAll('[data-creator-card]')];
      this.viewport = this.querySelector('[data-creator-viewport]');
      this.track = this.querySelector('[data-creator-track]');
      this.previousButton = this.querySelector('[data-creator-previous]');
      this.nextButton = this.querySelector('[data-creator-next]');
      this.sectionVisible = false;
      this.documentVisible = !document.hidden;
      const { signal } = this.abortController;

      this.ensureInstagramEmbeds();

      this.cards.forEach((card) => {
        const video = card.querySelector('video');
        const control = card.querySelector('[data-creator-toggle]');
        if (!video || !control) return;

        syncVideoPoster(video);

        control.addEventListener(
          'click',
          (event) => {
            event.stopPropagation();
            this.toggleVideo(card, video);
          },
          { signal },
        );
        card.addEventListener(
          'click',
          (event) => {
            if (event.target.closest('video, button, a')) return;
            this.toggleVideo(card, video);
          },
          { signal },
        );
        video.addEventListener(
          'play',
          () => {
            this.pauseAll(video);
            this.updateCard(card, video);
          },
          { signal },
        );
        video.addEventListener(
          'pause',
          () => this.updateCard(card, video),
          { signal },
        );
        video.addEventListener(
          'ended',
          () => {
            video.currentTime = 0;
            this.updateCard(card, video);
          },
          { signal },
        );
      });

      this.previousButton?.addEventListener(
        'click',
        () => this.scrollByCard(-1),
        { signal },
      );
      this.nextButton?.addEventListener(
        'click',
        () => this.scrollByCard(1),
        { signal },
      );
      this.track?.addEventListener(
        'scroll',
        () => window.requestAnimationFrame(() => this.updateNavigation()),
        { signal, passive: true },
      );

      window.addEventListener(
        'resize',
        () => {
          this.querySelectorAll('video').forEach(syncVideoPoster);
          this.updateNavigation();
        },
        { signal },
      );

      document.addEventListener(
        'visibilitychange',
        () => {
          this.documentVisible = !document.hidden;
          if (
            shouldPauseCreatorVideos({
              sectionVisible: this.sectionVisible,
              documentVisible: this.documentVisible,
            })
          ) {
            this.pauseAll();
          }
        },
        { signal },
      );

      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.sectionVisible = Boolean(entry?.isIntersecting);
          if (this.sectionVisible) {
            this.ensureSources();
          } else {
            this.pauseAll();
          }
        },
        {
          rootMargin: '200px 0px',
          threshold: 0.01,
        },
      );
      this.observer.observe(this);
      this.updateNavigation();
    }

    disconnectedCallback() {
      this.pauseAll();
      this.observer?.disconnect();
      this.abortController?.abort();
      this.abortController = null;
    }

    ensureSources() {
      this.querySelectorAll('video[data-src]').forEach((video) => {
        syncVideoPoster(video);
        if (video.currentSrc || video.src) return;
        video.src = video.dataset.src;
        video.load();
      });
    }

    ensureInstagramEmbeds() {
      if (!this.querySelector('[data-instgrm-permalink]')) return;

      const processEmbeds = () => window.instgrm?.Embeds?.process();
      const existingScript = document.querySelector('script[data-aceii-instagram-embed]');

      if (window.instgrm?.Embeds) {
        processEmbeds();
        return;
      }

      if (existingScript) {
        existingScript.addEventListener('load', processEmbeds, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.instagram.com/embed.js';
      script.dataset.aceiiInstagramEmbed = '';
      script.addEventListener('load', processEmbeds, { once: true });
      document.head.append(script);
    }

    scrollByCard(direction) {
      const firstCard = this.cards[0];
      if (!this.track || !firstCard) return;

      const styles = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      this.track.scrollBy({
        left: direction * (firstCard.getBoundingClientRect().width + gap),
        behavior: 'smooth',
      });
    }

    updateNavigation() {
      if (!this.track) return;

      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft >= maxScroll - 2;

      if (this.previousButton) this.previousButton.disabled = atStart;
      if (this.nextButton) this.nextButton.disabled = atEnd;
      this.querySelector('[data-creator-nav]')?.toggleAttribute('hidden', maxScroll <= 2);
    }

    async toggleVideo(card, video) {
      if (video.paused) {
        this.ensureSources();
        this.pauseAll(video);
        video.controls = true;

        try {
          await video.play();
          this.removeAttribute('data-playback-blocked');
        } catch {
          video.controls = false;
          this.setAttribute('data-playback-blocked', 'true');
          this.updateCard(card, video);
        }
      } else {
        video.pause();
      }
    }

    pauseAll(exceptVideo = null) {
      this.cards.forEach((card) => {
        const video = card.querySelector('video');
        if (!video || video === exceptVideo) return;
        if (!video.paused) video.pause();
        video.controls = false;
        this.updateCard(card, video);
      });
    }

    updateCard(card, video) {
      const playing = !video.paused && !video.ended;
      const control = card.querySelector('[data-creator-toggle]');

      card.classList.toggle('is-playing', playing);
      video.controls = playing;

      if (control) {
        control.setAttribute('aria-pressed', String(playing));
        control.setAttribute(
          'aria-label',
          playing ? 'Pause creator video' : 'Play creator video',
        );
      }
    }
  }

  window.customElements.define(elementName, AceiiCreatorReactions);
}
